import { useState, useTransition } from 'react';
import { cn } from '@c2fo/liquidity';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import { useCursorPagination } from '@/utils/useCursorPagination';
import AmountTabs from './AmountTabs';
import { EditInvoicesReferrer } from './EditInvoices';
import EditInvoicesTable, { EditInvoicesTableSort } from './EditInvoicesTable';

export interface EditInvoicesLayoutProps {
  asModal: boolean;
  hasSidebar?: boolean;
  readOnly?: boolean;
  referrer: EditInvoicesReferrer;
  scrollContainer?: HTMLDivElement | null;
  setShowInvoiceCriteriaModal: (open: boolean) => void;
  subscribeToMarketStats: boolean;
  takerMarket: TakerMarket | null;
}

const EditInvoicesLayout = ({
  asModal,
  hasSidebar,
  readOnly,
  referrer,
  scrollContainer,
  setShowInvoiceCriteriaModal,
  subscribeToMarketStats,
  takerMarket,
}: EditInvoicesLayoutProps) => {
  const [transition, startTransition] = useTransition();
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [tab, setTab] = useState<'included' | 'excluded'>('included');
  const [sort, setSort] = useState<EditInvoicesTableSort>({ key: 'amount', direction: 'desc' });
  const [isToolbarFloating, setIsToolbarFloating] = useState(false);
  const { currentCursor, currentPage, canGoBack, goToNextPage, goToPreviousPage, reset } = useCursorPagination();

  const handleSearch = (search: string) => {
    startTransition(() => {
      setSearchValue(search);
      reset(); // reset pagination when searching
    });
  };

  const handleSort = (newSort: EditInvoicesTableSort) => {
    startTransition(() => {
      setSort(newSort);
      reset(); // reset pagination when sorting
    });
  };

  const handleTabChange = (newTab: 'included' | 'excluded') => {
    setTab(newTab);
    reset(); // reset pagination when changing tabs
  };

  const handleNextPage = (nextCursor: string) => {
    startTransition(() => {
      goToNextPage(nextCursor);
    });
  };

  const handlePreviousPage = () => {
    startTransition(() => {
      goToPreviousPage();
    });
  };

  if (!takerMarket) {
    return null;
  }

  return (
    <IntercomDataTarget target="invoiceEditArea">
      <AmountTabs
        activeTab={tab}
        handleSearch={handleSearch}
        searchInput={searchInput}
        searchValue={searchValue}
        setActiveTab={handleTabChange}
        setSearchInput={setSearchInput}
        takerMarket={takerMarket}
      />
      <div
        className={cn('mb-0 overflow-hidden rounded-b-xl border border-t-0 border-stroke', {
          'mb-[120px]': asModal && isToolbarFloating,
        })}
      >
        <EditInvoicesTable
          asModal={asModal}
          hasSidebar={hasSidebar}
          isPending={transition}
          onSort={handleSort}
          onToolbarFloatingChange={setIsToolbarFloating}
          pagination={{
            currentCursor,
            currentPage,
            canGoBack,
            onNextPage: handleNextPage,
            onPreviousPage: handlePreviousPage,
          }}
          readOnly={readOnly}
          referrer={referrer}
          scrollContainer={scrollContainer}
          search={searchValue}
          setShowInvoiceCriteriaModal={setShowInvoiceCriteriaModal}
          sort={sort}
          subscribeToMarketStats={subscribeToMarketStats}
          tableType={tab}
          takerMarket={takerMarket}
        />
      </div>
    </IntercomDataTarget>
  );
};

export default EditInvoicesLayout;
